import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../shared/forms';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { ValidationError } from 'yup';
import { AlertComponent, CreateBusinessFormBody, createBusinessFormSchema, Button, IDaySchedule, createDefaultWeekSchedule, scheduleToApiDto } from '../../shared/components';
import { PLANS_DATA } from '../../shared/constants/plans';
import { buildAddress } from '../../shared/helpers/buildAddress';
import getValidationErrors from '../../shared/helpers/getValidationErrors';
import { LayoutRegister } from '../../shared/layouts';
import {
  ICreateUser,
  ILogin,
  authService,
  businessService,
  feedback,
} from '../../shared/services';
import type { ICreateBusiness } from '../../shared/services/api/businessService';
import { useUserContext } from '../../shared/contexts';
import { Login } from '../../shared/components/SconApp/login/components/Login';
import { Register } from '../../shared/components/SconApp/login/components/Register';

const PENDING_TRIAL_KEY = 'PENDING_TRIAL_DATA';

interface IPendingTrialData {
  name: string;
  address: string;
  cnpj?: string;
  planSlug: string;
  phone?: string;
  instagram?: string;
  facebook?: string;
  website?: string;
  paymentMethods: string[];
  weeklySchedules: IDaySchedule[];
}

export const CreateBusinessTrial: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialSlug = searchParams.get('plan') || 'solo';
  const initialCycle = searchParams.get('cycle') || 'monthly';
  const [selectedPlanSlug, setSelectedPlanSlug] = useState(initialSlug);
  const planLabel = PLANS_DATA.find(p => p.slug === selectedPlanSlug)?.name || selectedPlanSlug;

  const planForTrialRef = useRef(initialSlug);
  const billingCycleRef = useRef(initialCycle);

  // Keep ref in sync with user's plan selection (used by mutation closures)
  useEffect(() => {
    planForTrialRef.current = selectedPlanSlug;
  }, [selectedPlanSlug]);

  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const { isAuthenticated, login, googleLogin, create } = useUserContext();

  const isAutoSubmitting = useRef(false);

  const [schedule, setSchedule] = useState<IDaySchedule[]>(createDefaultWeekSchedule);
  const [createdBusinessId, setCreatedBusinessId] = useState<number | null>(null);
  const [trialError, setTrialError] = useState<string | null>(null);

  // ── Modal login/register state ──
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModal, setIsRegisterModal] = useState(true);
  const [modalErrorMessage, setModalErrorMessage] = useState<string>();
  const [modalShowPassword, setModalShowPassword] = useState(false);

  // Google login for the modal
  const googleLoginWithBirthday = useGoogleLogin({
    flow: 'implicit',
    scope: 'openid email profile https://www.googleapis.com/auth/user.birthday.read',
    onSuccess: (tokenResponse) => {
      mutateModalGoogleLogin(tokenResponse.access_token);
    },
    onError: () => console.log('Erro ao logar com Google'),
  });

  // ── Retry MP checkout if the first redirect failed ──
  const retryTrialCallback = useCallback(() => {
    if (!createdBusinessId) throw new Error('Nenhum business criado');
    navigate(`/subscription/checkout?businessId=${createdBusinessId}&plan=${planForTrialRef.current}&cycle=${billingCycleRef.current}`);
  }, [createdBusinessId, navigate]);

  const { mutate: retryTrial } = useMutation({
    mutationFn: async () => {
      retryTrialCallback();
    },
    onSuccess: async () => {
      // Navigation is handled inside retryTrialCallback
    },
    onError: (error: Error) => {
      setTrialError(error.message);
      feedback(error.message, 'error');
    },
  });

  // ── Business creation + trial ──
  const { mutate: createBusiness, isLoading: isLoadingCreate } = useMutation({
    mutationFn: (data: ICreateBusiness) => businessService.create(data),
    onSuccess: (businessId: number) => {
      setCreatedBusinessId(businessId);
      // Redirect to MP checkout — the plan has 30-day trial configured in MP dashboard
      navigate(`/subscription/checkout?businessId=${businessId}&plan=${planForTrialRef.current}&cycle=${billingCycleRef.current}`);
    },
    onError: (error: Error) => {
      feedback(error.message, 'error');
    },
    onSettled: () => {
      isAutoSubmitting.current = false;
    },
  });

  // ── Modal mutations ──
  const { mutate: mutateModalLogin, isLoading: isModalLoginLoading } = useMutation(
    (user: ILogin) => login(user),
    {
      onSuccess: () => {
        setIsLoginModalOpen(false);
        handleAutoSubmit();
      },
      onError: (error: Error) => setModalErrorMessage(error.message.substring(0, 120)),
    }
  );

  const { mutate: mutateModalGoogleLogin, isLoading: isModalGoogleLoading } = useMutation(
    (jwt: string) => googleLogin(jwt),
    {
      onSuccess: () => {
        setIsLoginModalOpen(false);
        handleAutoSubmit();
      },
      onError: (error: Error) => setModalErrorMessage(error.message.substring(0, 120)),
    }
  );

  const { mutate: mutateModalCreate, isLoading: isModalCreateLoading } = useMutation(
    (user: ICreateUser) => create(user),
    {
      onSuccess: () => {
        feedback('Usuário criado', 'success');
        setIsLoginModalOpen(false);
        handleAutoSubmit();
      },
      onError: (error: Error) => setModalErrorMessage(error.message.substring(0, 120)),
    }
  );

  const isModalLoading = isModalLoginLoading || isModalCreateLoading || isModalGoogleLoading;

  // ── Auto-submit: read pending data from localStorage, create business + trial ──
  const handleAutoSubmit = useCallback(() => {
    if (isAutoSubmitting.current) return;
    isAutoSubmitting.current = true;

    const raw = localStorage.getItem(PENDING_TRIAL_KEY);
    if (!raw) {
      isAutoSubmitting.current = false;
      return;
    }

    let pending: IPendingTrialData;
    try {
      pending = JSON.parse(raw);
    } catch {
      localStorage.removeItem(PENDING_TRIAL_KEY);
      isAutoSubmitting.current = false;
      return;
    }
    localStorage.removeItem(PENDING_TRIAL_KEY);

    if (pending.planSlug) {
      planForTrialRef.current = pending.planSlug;
      setSelectedPlanSlug(pending.planSlug);
    }

    const business: ICreateBusiness = {
      name: pending.name,
      address: pending.address,
      phone: pending.phone || undefined,
      instagram: pending.instagram || undefined,
      facebook: pending.facebook || undefined,
      website: pending.website || undefined,
      paymentMethods:
        pending.paymentMethods.length > 0
          ? pending.paymentMethods.join(', ')
          : undefined,
      planSlug: pending.planSlug || undefined,
      weeklySchedules: scheduleToApiDto(pending.weeklySchedules),
    };

    createBusiness(business);
  }, [createBusiness]);

  // ── On mount / auth hydration: check for pending data ──
  useEffect(() => {
    if (isAuthenticated && localStorage.getItem(PENDING_TRIAL_KEY)) {
      handleAutoSubmit();
    }
  }, [isAuthenticated, handleAutoSubmit]);

  // ── Cancel modal: clear pending data and go back to form ──
  const handleCancelModal = useCallback(() => {
    localStorage.removeItem(PENDING_TRIAL_KEY);
    setIsLoginModalOpen(false);
    setModalErrorMessage(undefined);
    setIsRegisterModal(true);
    setModalShowPassword(false);
  }, []);

  // ── Toggle login/register inside modal ──
  const handleToggleModalAuth = useCallback(() => {
    setModalErrorMessage(undefined);
    setIsRegisterModal((prev) => !prev);
    setModalShowPassword(false);
  }, []);

  // ── Form submit ──
  const handleSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      try {
        formRef.current?.setErrors({});

        await createBusinessFormSchema.validate(data, { abortEarly: false });

        const weeklySchedules = scheduleToApiDto(schedule);


        const business: ICreateBusiness = {
          name: data.name as string,
          address: buildAddress(data),
          cnpj: (data.cnpj as string) || undefined,
          phone: (data.phone as string) || undefined,
          instagram: (data.instagram as string) || undefined,
          facebook: (data.facebook as string) || undefined,
          website: (data.website as string) || undefined,
          paymentMethods: data.paymentMethods as string | undefined,
          planSlug: selectedPlanSlug,
          weeklySchedules,
        };

        if (!isAuthenticated) {
          const pending: IPendingTrialData = {
            name: data.name as string,
            address: buildAddress(data),
            cnpj: (data.cnpj as string) || undefined,
            planSlug: selectedPlanSlug,
            phone: (data.phone as string) || undefined,
            instagram: (data.instagram as string) || undefined,
            facebook: (data.facebook as string) || undefined,
            website: (data.website as string) || undefined,
            paymentMethods: data.paymentMethods
              ? (data.paymentMethods as string).split(', ')
              : [],
            weeklySchedules: schedule,
          };
          localStorage.setItem(PENDING_TRIAL_KEY, JSON.stringify(pending));
          setIsLoginModalOpen(true);
          return;
        }

        // Validate the cookie is still valid — isAuthenticated may be stale
        // from localStorage cache when the user was previously logged in
        // but the JWT cookie has expired.
        try {
          await authService.getUserFromCookie();
        } catch {
          const pending: IPendingTrialData = {
            name: data.name as string,
            address: buildAddress(data),
            cnpj: (data.cnpj as string) || undefined,
            planSlug: selectedPlanSlug,
            phone: (data.phone as string) || undefined,
            instagram: (data.instagram as string) || undefined,
            facebook: (data.facebook as string) || undefined,
            website: (data.website as string) || undefined,
            paymentMethods: data.paymentMethods
              ? (data.paymentMethods as string).split(', ')
              : [],
            weeklySchedules: schedule,
          };
          localStorage.setItem(PENDING_TRIAL_KEY, JSON.stringify(pending));
          setIsLoginModalOpen(true);
          return;
        }

        createBusiness(business);
      } catch (err) {
        if (err instanceof ValidationError) {
          const errors = getValidationErrors(err as ValidationError);
          formRef.current?.setErrors(errors);
        }
      }
    },
    [createBusiness, schedule, selectedPlanSlug, isAuthenticated]
  );

  if (trialError && createdBusinessId) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        padding={3}
      >
        <AlertComponent />
        <Box
          display="flex"
          flexDirection="column"
          gap={3}
          padding={3}
          maxWidth={500}
          textAlign="center"
        >
          <Typography variant="h5" fontWeight="bold" color="warning.main">
            Trial não ativado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            O estabelecimento foi criado, mas houve um erro ao ativar o período de teste.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {trialError}
          </Typography>
          <Box display="flex" gap={2} justifyContent="center" mt={2}>
            <Button variant="contained" onClick={() => retryTrial()}>
              Tentar novamente
            </Button>
            <Button variant="outlined" onClick={() => navigate('/business')}>
              Ir para o estabelecimento
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <AlertComponent />
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100dvh"
        bgcolor="background.default"
        padding={3}
      >
        <Form ref={formRef} onSubmit={handleSubmit}>
          <LayoutRegister
            isLoading={isLoadingCreate}
            saveButtonLabel="Criar estabelecimento"
            isSubmit
          >
            <Box display="flex" flexDirection="column" gap={3} padding={3}>
              <Typography variant="h5" fontWeight="bold">
                Teste Grátis — Plano {planLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                São 30 dias grátis para testar todos os recursos do plano {planLabel}. Sem cartão de crédito.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {PLANS_DATA.map((plan) => (
                  <Paper
                    key={plan.slug}
                    onClick={() => setSelectedPlanSlug(plan.slug)}
                    sx={{
                      flex: 1,
                      minWidth: 140,
                      p: 2,
                      cursor: 'pointer',
                      textAlign: 'center',
                      border: 2,
                      borderColor: selectedPlanSlug === plan.slug ? 'primary.main' : 'transparent',
                      bgcolor: selectedPlanSlug === plan.slug ? 'action.selected' : 'background.paper',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: 'primary.light', boxShadow: 2 },
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={700}>
                      {plan.name}
                    </Typography>
                    <Typography variant="caption" color="primary.main" fontWeight={600} display="block">
                      R$ {plan.priceMonthly}/mês
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {plan.description}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <CreateBusinessFormBody
                schedule={schedule}
                onScheduleChange={setSchedule}
              />
            </Box>
          </LayoutRegister>
        </Form>
      </Box>

      {/* ── Login/cadastro modal ── */}
      <Dialog open={isLoginModalOpen} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle textAlign="center">Faça login para finalizar seu cadastro</DialogTitle>
        <Divider />
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {isRegisterModal ? (
              <Register
                errorMessage={modalErrorMessage}
                showPassword={modalShowPassword}
                setShowPassword={setModalShowPassword}
                mutateCreate={mutateModalCreate}
                isLoadingCreate={isModalLoading}
              />
            ) : (
              <Login
                errorMessage={modalErrorMessage}
                setErrorMessage={setModalErrorMessage}
                showPassword={modalShowPassword}
                setShowPassword={setModalShowPassword}
                mutateLogin={mutateModalLogin}
                isLoadingLogin={isModalLoading}
              />
            )}

            <Button
              label={isRegisterModal ? 'Cadastrar' : 'Logar'}
              variant="contained"
              disabled={isModalLoading}
              isLoading={isModalLoading}
              type="submit"
              form={isRegisterModal ? 'register-form' : 'login-form'}
            />

            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={2}>
              <Typography>ou continue com</Typography>
              <Button
                label="Google"
                variant="outlined"
                onClick={() => googleLoginWithBirthday()}
                disabled={isModalLoading}
                startIcon={
                  <svg width="18" height="18" viewBox="0 0 48 48">
                    <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
                    <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
                    <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
                    <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
                  </svg>
                }
              >
                Entrar com Google
              </Button>
            </Box>

            <Divider variant="middle" />
            <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
              <Typography fontSize={14}>
                {isRegisterModal ? 'Já' : 'Não'} possui uma conta?
              </Typography>
              <Button
                label={isRegisterModal ? 'Login' : 'Cadastre-se'}
                variant="text"
                size="small"
                disabled={isModalLoading}
                onClick={handleToggleModalAuth}
              />
            </Box>
            <Box display="flex" justifyContent="center">
              <Button
                label="Cancelar"
                variant="text"
                size="small"
                disabled={isModalLoading}
                onClick={handleCancelModal}
                style={{ color: 'text.secondary' }}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
